// src/pages/dashboard/AdminEmployees.jsx
import ReactDOM from "react-dom";
import React, { useEffect, useMemo, useState } from "react";
import {
  getEmployees,
  getEmployee,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  downloadOfferLetter,
  downloadExperienceLetter,
} from "../../core/api/employeeApi";

function cls(...arr) {
  return arr.filter(Boolean).join(" ");
}

function safeStr(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function formatDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return v;
  }
}

function badgeClass(status) {
  const s = safeStr(status).toUpperCase();
  if (s === "ACTIVE") return "bg-green-100 text-green-700 border-green-200";
  if (s === "RESIGNED") return "bg-red-100 text-red-700 border-red-200";
  if (s === "INACTIVE") return "bg-gray-100 text-gray-700 border-gray-200";
  return "bg-yellow-100 text-yellow-800 border-yellow-200";
}

function normalizeListResponse(data) {
  // Supports:
  // 1) paginated: {count,next,previous,results:[...]}
  // 2) array: [...]
  // 3) single object: {...}
  if (!data) return { items: [], count: 0, next: null, previous: null };
  if (Array.isArray(data)) {
    return { items: data, count: data.length, next: null, previous: null };
  }
  if (Array.isArray(data.results)) {
    return {
      items: data.results,
      count: data.count ?? data.results.length,
      next: data.next ?? null,
      previous: data.previous ?? null,
    };
  }
  if (typeof data === "object") {
    return { items: [data], count: 1, next: null, previous: null };
  }
  return { items: [], count: 0, next: null, previous: null };
}

function saveBlobAsFile(blobData, filename) {
  const blob = new Blob([blobData], { type: "application/pdf" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(link.href);
}

/** ✅ Employee ID helper: shows MJ-0001 format always */
function getEmployeeCode(empOrId) {
  if (!empOrId) return "MJ-0000";

  if (typeof empOrId === "object") {
    if (empOrId.employee_id) return empOrId.employee_id;
    if (empOrId.id) return `MJ-${String(empOrId.id).padStart(4, "0")}`;
    return "MJ-0000";
  }

  return `MJ-${String(empOrId).padStart(4, "0")}`;
}

const initialForm = {
  employee_photo: null, // File OR null (existing url separately store)

  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  phone_number: "",

  date_of_joining: "",
  last_working_date: "",

  employment_type: "FULL_TIME",
  department: "",
  designation: "",
  status: "ACTIVE",

  total_experience_years: "0",
  primary_skills: "", // comma separated in UI
  education: "",

  pan_number: "",
  address: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",

  salary_basic: "0",
  salary_hra: "0",
  salary_special_allowance: "0",
  salary_bonus: "0",
  salary_deductions: "0",
  in_hand_salary: "",
  currency: "INR",

  date_of_birth: "",
  gender: "",
  aadhar_number: "",

  bank_name: "",
  bank_account_no: "",
  ifsc_code: "",

  probation_months: "3",
  work_location: "",
  reporting_manager_name: "",
  notes: "",
};

function SectionTitle({ children }) {
  return (
    <div className="text-secondary font-semibold text-sm uppercase tracking-wide">
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-secondary/70">{label}</div>
      <div className="text-sm text-secondary">{value || "—"}</div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-secondary/70">{label}</span>
      <input
        {...props}
        className={cls(
          "w-full rounded-xl border border-secondary/25 bg-white/70",
          "px-3 py-2 text-sm text-secondary outline-none",
          "focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
        )}
      />
    </label>
  );
}

function Select({ label, children, ...props }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-secondary/70">{label}</span>
      <select
        {...props}
        className={cls(
          "w-full rounded-xl border border-secondary/25 bg-white/70",
          "px-3 py-2 text-sm text-secondary outline-none",
          "focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
        )}
      >
        {children}
      </select>
    </label>
  );
}

function Textarea({ label, ...props }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-secondary/70">{label}</span>
      <textarea
        {...props}
        className={cls(
          "w-full rounded-xl border border-secondary/25 bg-white/70",
          "px-3 py-2 text-sm text-secondary outline-none min-h-[90px]",
          "focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
        )}
      />
    </label>
  );
}

function Modal({ open, title, onClose, children, widthClass = "max-w-4xl" }) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999]">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* viewport scroll container */}
      <div className="relative h-full w-full overflow-y-auto">
        {/* modal wrapper */}
        <div className="min-h-full flex items-start sm:items-center justify-center p-4 sm:p-6">
          <div
            className={cls(
              "w-full",
              widthClass,
              "rounded-2xl bg-white shadow-2xl border border-secondary/20 overflow-hidden",
              "max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]"
            )}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-secondary/15 bg-white/80">
              <div className="text-secondary font-semibold">{title}</div>
              <button
                className="rounded-xl border border-secondary/25 px-3 py-1 text-secondary hover:bg-primary/10 transition"
                onClick={onClose}
              >
                ✕
              </button>
            </div>

            {/* content scrolls inside */}
            <div className="p-5 overflow-y-auto max-h-[calc(100vh-7rem)]">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmModal({ open, title, message, onClose, onConfirm, loading }) {
  return (
    <Modal open={open} title={title} onClose={onClose} widthClass="max-w-lg">
      <div className="space-y-4">
        <div className="text-sm text-secondary/80">{message}</div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-xl border border-secondary/30 px-4 py-2 text-sm text-secondary bg-white/70 hover:bg-primary/10 transition disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="rounded-xl bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:opacity-95 transition disabled:opacity-70"
          >
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function toFormFromEmployee(emp) {
  const skills = Array.isArray(emp?.primary_skills)
    ? emp.primary_skills.join(", ")
    : "";
  return {
    ...initialForm,
    employee_photo: null, // file only if user re-uploads
    first_name: safeStr(emp?.first_name),
    middle_name: safeStr(emp?.middle_name),
    last_name: safeStr(emp?.last_name),
    email: safeStr(emp?.email),
    phone_number: safeStr(emp?.phone_number),

    date_of_joining: safeStr(emp?.date_of_joining),
    last_working_date: safeStr(emp?.last_working_date),

    employment_type: safeStr(emp?.employment_type) || "FULL_TIME",
    department: safeStr(emp?.department),
    designation: safeStr(emp?.designation),
    status: safeStr(emp?.status) || "ACTIVE",

    total_experience_years: safeStr(emp?.total_experience_years || "0"),
    primary_skills: skills,
    education: safeStr(emp?.education),

    pan_number: safeStr(emp?.pan_number),
    address: safeStr(emp?.address),
    emergency_contact_name: safeStr(emp?.emergency_contact_name),
    emergency_contact_phone: safeStr(emp?.emergency_contact_phone),

    salary_basic: safeStr(emp?.salary_basic || "0"),
    salary_hra: safeStr(emp?.salary_hra || "0"),
    salary_special_allowance: safeStr(emp?.salary_special_allowance || "0"),
    salary_bonus: safeStr(emp?.salary_bonus || "0"),
    salary_deductions: safeStr(emp?.salary_deductions || "0"),
    in_hand_salary: safeStr(emp?.in_hand_salary || ""),
    currency: safeStr(emp?.currency || "INR"),

    date_of_birth: safeStr(emp?.date_of_birth),
    gender: safeStr(emp?.gender),
    aadhar_number: safeStr(emp?.aadhar_number),

    bank_name: safeStr(emp?.bank_name),
    bank_account_no: safeStr(emp?.bank_account_no),
    ifsc_code: safeStr(emp?.ifsc_code),

    probation_months: safeStr(emp?.probation_months || "3"),
    work_location: safeStr(emp?.work_location),
    reporting_manager_name: safeStr(emp?.reporting_manager_name),
    notes: safeStr(emp?.notes),
  };
}

function buildMultipartPayload(form) {
  const fd = new FormData();

  if (form.employee_photo instanceof File) {
    fd.append("employee_photo", form.employee_photo);
  }

  const skillsArr = safeStr(form.primary_skills)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const payload = {
    ...form,
    primary_skills: skillsArr,
  };

  Object.entries(payload).forEach(([k, v]) => {
    if (k === "employee_photo") return;
    if (v === "" || v === null || v === undefined) return;

    if (k === "primary_skills") {
      fd.append(k, JSON.stringify(v));
      return;
    }
    fd.append(k, v);
  });

  return fd;
}

export default function AdminEmployees() {
  // list state
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  // filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [department, setDepartment] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [employee, setEmployee] = useState(null);

  // create/edit modal
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");

  // delete modal
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const totalPages = useMemo(() => {
    if (!count) return 1;
    return Math.max(1, Math.ceil(count / pageSize));
  }, [count]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (q?.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (department?.trim()) params.department = department.trim();

      const res = await getEmployees(params);
      const normalized = normalizeListResponse(res.data);
      setItems(normalized.items || []);
      setCount(normalized.count || 0);
    } catch {
      setItems([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const applyFilters = () => {
    setPage(1);
    setTimeout(fetchEmployees, 0);
  };

  const openDetail = async (id) => {
    try {
      setSelectedId(id);
      setDetailOpen(true);
      setDetailLoading(true);
      setEmployee(null);

      const res = await getEmployee(id);
      setEmployee(res.data);
    } catch {
      setEmployee(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
    setEmployee(null);
  };

  const onFormChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const resetForm = () => {
    setForm(initialForm);
    setExistingPhotoUrl("");
    setFormError("");
  };

  const openCreate = () => {
    resetForm();
    setFormMode("create");
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      setFormError("");
      setSaving(false);
      setFormMode("edit");
      setFormOpen(true);

      const res = await getEmployee(id);
      setSelectedId(id);
      setExistingPhotoUrl(res.data?.employee_photo || "");
      setForm(toFormFromEmployee(res.data));
    } catch {
      setFormError("Failed to load employee for edit.");
    }
  };

  const submitCreate = async () => {
    setFormError("");
    try {
      setSaving(true);
      const fd = buildMultipartPayload(form);

      await createEmployee(fd);

      setFormOpen(false);
      resetForm();
      setPage(1);
      fetchEmployees();
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === "object") {
        const firstKey = Object.keys(data)[0];
        const firstVal = data[firstKey];
        const msg = Array.isArray(firstVal) ? firstVal[0] : safeStr(firstVal);
        setFormError(`${firstKey}: ${msg}`);
      } else {
        setFormError("Failed to create employee. Please check inputs.");
      }
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!selectedId) return;
    setFormError("");
    try {
      setSaving(true);
      const fd = buildMultipartPayload(form);

      await updateEmployee(selectedId, fd);

      setFormOpen(false);
      resetForm();
      fetchEmployees();

      if (detailOpen && selectedId) {
        openDetail(selectedId);
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === "object") {
        const firstKey = Object.keys(data)[0];
        const firstVal = data[firstKey];
        const msg = Array.isArray(firstVal) ? firstVal[0] : safeStr(firstVal);
        setFormError(`${firstKey}: ${msg}`);
      } else {
        setFormError("Failed to update employee. Please check inputs.");
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (emp) => {
    setDeleteTarget(emp);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await deleteEmployee(deleteTarget.id);
      setDeleteOpen(false);
      setDeleteTarget(null);
      fetchEmployees();
      if (detailOpen && selectedId === deleteTarget.id) closeDetail();
    } catch {
      setDeleteOpen(false);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  /** ✅ filenames use MJ-0001 format (if backend sends employee_id it will use that) */
  const downloadOffer = async (id) => {
    const row = items.find((x) => x.id === id);
    const code = getEmployeeCode(row || employee || id);
    const res = await downloadOfferLetter(id);
    saveBlobAsFile(res.data, `OfferLetter_${code}.pdf`);
  };

  const downloadExperience = async (id) => {
    const row = items.find((x) => x.id === id);
    const code = getEmployeeCode(row || employee || id);
    const res = await downloadExperienceLetter(id);
    saveBlobAsFile(res.data, `ExperienceLetter_${code}.pdf`);
  };

  const formTitle = formMode === "create" ? "Add Employee" : "Edit Employee";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-secondary font-bold text-xl">Employees</div>
          <div className="text-sm text-secondary/70">
            Manage employee records, photos, salary & letters
          </div>
        </div>

        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-xl bg-secondary text-accent px-4 py-2 text-sm font-semibold hover:opacity-95 transition"
        >
          + Add Employee
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-secondary/20 bg-white/70 backdrop-blur-sm p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input
            label="Search (name/email/phone)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type to search…"
          />

          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
            <option value="RESIGNED">RESIGNED</option>
          </Select>

          <Input
            label="Department"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            placeholder="e.g. Engineering"
          />

          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="w-full rounded-xl border border-secondary/30 px-4 py-2 text-secondary bg-white/70 hover:bg-primary/10 transition text-sm font-semibold"
            >
              Apply
            </button>
            <button
              onClick={() => {
                setQ("");
                setStatus("");
                setDepartment("");
                setPage(1);
                setTimeout(fetchEmployees, 0);
              }}
              className="rounded-xl border border-secondary/30 px-4 py-2 text-secondary bg-white/70 hover:bg-primary/10 transition text-sm"
              title="Reset filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-secondary/20 bg-white/70 backdrop-blur-sm shadow-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-secondary/15 flex items-center justify-between">
          <div className="text-secondary font-semibold text-sm">
            Total: {count}
          </div>

          <div className="text-xs text-secondary/60">
            Page {page} / {totalPages}
          </div>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white/80">
              <tr className="text-left text-secondary/70">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Department</th>
                <th className="px-4 py-3 font-semibold">Designation</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Joining</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-secondary/10">
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-secondary/70" colSpan={7}>
                    Loading employees…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-secondary/70" colSpan={7}>
                    No employees found.
                  </td>
                </tr>
              ) : (
                items.map((e) => {
                  const id = e.id;
                  const name = [e.first_name, e.middle_name, e.last_name]
                    .filter(Boolean)
                    .join(" ");
                  const photo = e.employee_photo;

                  // ✅ THIS is what you wanted in screenshot:
                  const code = getEmployeeCode(e);

                  return (
                    <tr key={id} className="hover:bg-primary/5 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-xl overflow-hidden bg-secondary/10 border border-secondary/20">
                            {photo ? (
                              <img
                                src={photo}
                                alt={name}
                                className="h-full w-full object-cover"
                                onError={(ev) => {
                                  ev.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="h-full w-full grid place-items-center text-secondary/60 font-bold">
                                {name ? name.charAt(0).toUpperCase() : "E"}
                              </div>
                            )}
                          </div>

                          <div className="leading-tight">
                            <div className="text-secondary font-semibold">
                              {name || "—"}
                            </div>
                            <div className="text-xs text-secondary/70">
                              {e.employment_type || "—"} • ID: {code}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="text-secondary">{e.email || "—"}</div>
                        <div className="text-xs text-secondary/70">
                          {e.phone_number || "—"}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-secondary">
                        {e.department || "—"}
                      </td>

                      <td className="px-4 py-3 text-secondary">
                        {e.designation || "—"}
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={cls(
                            "inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold",
                            badgeClass(e.status)
                          )}
                        >
                          {e.status || "—"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-secondary">
                        {formatDate(e.date_of_joining)}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openDetail(id)}
                            className="rounded-xl border border-secondary/25 px-3 py-1.5 text-secondary bg-white/70 hover:bg-primary/10 transition text-xs font-semibold"
                          >
                            View
                          </button>

                          <button
                            onClick={() => openEdit(id)}
                            className="rounded-xl border border-secondary/25 px-3 py-1.5 text-secondary bg-white/70 hover:bg-primary/10 transition text-xs font-semibold"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => confirmDelete(e)}
                            className="rounded-xl border border-red-200 px-3 py-1.5 text-red-700 bg-red-50 hover:bg-red-100 transition text-xs font-semibold"
                          >
                            Delete
                          </button>

                          <button
                            onClick={() => downloadOffer(id)}
                            className="rounded-xl border border-secondary/25 px-3 py-1.5 text-secondary bg-white/70 hover:bg-primary/10 transition text-xs"
                          >
                            Offer PDF
                          </button>

                          <button
                            onClick={() => downloadExperience(id)}
                            className="rounded-xl border border-secondary/25 px-3 py-1.5 text-secondary bg-white/70 hover:bg-primary/10 transition text-xs"
                          >
                            Experience PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-secondary/15 flex items-center justify-between">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={cls(
              "rounded-xl border px-4 py-2 text-sm",
              page <= 1
                ? "border-secondary/20 text-secondary/40 bg-white/40 cursor-not-allowed"
                : "border-secondary/30 text-secondary bg-white/70 hover:bg-primary/10 transition"
            )}
          >
            ← Prev
          </button>

          <div className="text-xs text-secondary/60">
            Showing {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, count)} of {count}
          </div>

          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={cls(
              "rounded-xl border px-4 py-2 text-sm",
              page >= totalPages
                ? "border-secondary/20 text-secondary/40 bg-white/40 cursor-not-allowed"
                : "border-secondary/30 text-secondary bg-white/70 hover:bg-primary/10 transition"
            )}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        title="Employee Details"
        onClose={closeDetail}
        widthClass="max-w-5xl"
      >
        {detailLoading ? (
          <div className="text-secondary/70">Loading…</div>
        ) : !employee ? (
          <div className="text-secondary/70">No details found.</div>
        ) : (
          <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-2xl overflow-hidden bg-secondary/10 border border-secondary/20">
                  {employee.employee_photo ? (
                    <img
                      src={employee.employee_photo}
                      alt="employee"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-secondary/60 font-bold">
                      {(employee.first_name || "E").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-secondary font-bold text-lg">
                    {[employee.first_name, employee.middle_name, employee.last_name]
                      .filter(Boolean)
                      .join(" ")}
                  </div>

                  {/* ✅ THIS is what you wanted in screenshot: */}
                  <div className="text-sm text-secondary/70">
                    {employee.designation} • {employee.department} • ID:{" "}
                    {getEmployeeCode(employee || selectedId)}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span
                  className={cls(
                    "inline-flex items-center px-3 py-1.5 rounded-full border text-xs font-semibold",
                    badgeClass(employee.status)
                  )}
                >
                  {employee.status}
                </span>

                <button
                  onClick={() => downloadOffer(selectedId)}
                  className="rounded-xl bg-secondary text-accent px-4 py-2 text-xs font-semibold hover:opacity-95 transition"
                >
                  Download Offer
                </button>

                <button
                  onClick={() => downloadExperience(selectedId)}
                  className="rounded-xl border border-secondary/30 px-4 py-2 text-xs font-semibold text-secondary bg-white/70 hover:bg-primary/10 transition"
                >
                  Download Experience
                </button>

                <button
                  onClick={() => openEdit(selectedId)}
                  className="rounded-xl border border-secondary/30 px-4 py-2 text-xs font-semibold text-secondary bg-white/70 hover:bg-primary/10 transition"
                >
                  Edit
                </button>

                <button
                  onClick={() =>
                    confirmDelete({
                      ...employee,
                      id: selectedId,
                      employee_id: employee.employee_id,
                    })
                  }
                  className="rounded-xl border border-red-200 px-4 py-2 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 transition"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
                <SectionTitle>Personal</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Email" value={employee.email} />
                  <Field label="Phone" value={employee.phone_number} />
                  <Field label="DOB" value={formatDate(employee.date_of_birth)} />
                  <Field label="Gender" value={employee.gender} />
                  <Field label="Aadhaar" value={employee.aadhar_number} />
                  <Field label="Address" value={employee.address} />
                  <Field label="Education" value={employee.education} />
                  <Field label="PAN" value={employee.pan_number} />
                </div>
              </div>

              <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
                <SectionTitle>Employment</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Employment Type" value={employee.employment_type} />
                  <Field label="Status" value={employee.status} />
                  <Field label="Department" value={employee.department} />
                  <Field label="Designation" value={employee.designation} />
                  <Field label="Date of Joining" value={formatDate(employee.date_of_joining)} />
                  <Field label="Last Working Date" value={formatDate(employee.last_working_date)} />
                  <Field label="Probation (months)" value={safeStr(employee.probation_months)} />
                  <Field label="Work Location" value={employee.work_location} />
                  <Field label="Reporting Manager" value={employee.reporting_manager_name} />
                  <Field label="Experience (years)" value={safeStr(employee.total_experience_years)} />
                </div>

                <div className="pt-1">
                  <div className="text-xs text-secondary/70">Primary Skills</div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(employee.primary_skills || []).length ? (
                      employee.primary_skills.map((s, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs border border-secondary/20 bg-primary/10 text-secondary"
                        >
                          {s}
                        </span>
                      ))
                    ) : (
                      <div className="text-sm text-secondary">—</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
                <SectionTitle>Salary</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Basic" value={`${employee.currency} ${employee.salary_basic}`} />
                  <Field label="HRA" value={`${employee.currency} ${employee.salary_hra}`} />
                  <Field
                    label="Special Allowance"
                    value={`${employee.currency} ${employee.salary_special_allowance}`}
                  />
                  <Field label="Bonus" value={`${employee.currency} ${employee.salary_bonus}`} />
                  <Field label="Deductions" value={`${employee.currency} ${employee.salary_deductions}`} />
                  <Field label="In-hand (Monthly)" value={`${employee.currency} ${employee.in_hand_salary}`} />
                </div>
                <Field label="Notes" value={employee.notes} />
              </div>

              <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
                <SectionTitle>Bank / Emergency</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Bank Name" value={employee.bank_name} />
                  <Field label="Account No" value={employee.bank_account_no} />
                  <Field label="IFSC" value={employee.ifsc_code} />
                  <Field label="Emergency Contact" value={employee.emergency_contact_name} />
                  <Field label="Emergency Phone" value={employee.emergency_contact_phone} />
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        open={formOpen}
        title={formTitle}
        onClose={() => setFormOpen(false)}
        widthClass="max-w-5xl"
      >
        <div className="space-y-4">
          {formError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
              {formError}
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
              <SectionTitle>Basic</SectionTitle>

              {formMode === "edit" && existingPhotoUrl ? (
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl overflow-hidden border border-secondary/20 bg-secondary/10">
                    <img
                      src={existingPhotoUrl}
                      alt="current"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="text-xs text-secondary/70">
                    Current Photo (upload new to replace)
                  </div>
                </div>
              ) : null}

              <label className="block space-y-1">
                <span className="text-xs text-secondary/70">Employee Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    onFormChange("employee_photo", e.target.files?.[0] || null)
                  }
                  className="w-full rounded-xl border border-secondary/25 bg-white/70 px-3 py-2 text-sm text-secondary"
                />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="First Name *"
                  value={form.first_name}
                  onChange={(e) => onFormChange("first_name", e.target.value)}
                />
                <Input
                  label="Middle Name"
                  value={form.middle_name}
                  onChange={(e) => onFormChange("middle_name", e.target.value)}
                />
                <Input
                  label="Last Name *"
                  value={form.last_name}
                  onChange={(e) => onFormChange("last_name", e.target.value)}
                />
                <Input
                  label="Email *"
                  value={form.email}
                  onChange={(e) => onFormChange("email", e.target.value)}
                />
                <Input
                  label="Phone *"
                  value={form.phone_number}
                  onChange={(e) => onFormChange("phone_number", e.target.value)}
                />
                <Input
                  label="PAN *"
                  value={form.pan_number}
                  onChange={(e) =>
                    onFormChange("pan_number", e.target.value.toUpperCase())
                  }
                />
              </div>

              <Textarea
                label="Address"
                value={form.address}
                onChange={(e) => onFormChange("address", e.target.value)}
              />
            </div>

            <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
              <SectionTitle>Employment</SectionTitle>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Date of Joining *"
                  type="date"
                  value={form.date_of_joining}
                  onChange={(e) =>
                    onFormChange("date_of_joining", e.target.value)
                  }
                />
                <Input
                  label="Last Working Date"
                  type="date"
                  value={form.last_working_date}
                  onChange={(e) =>
                    onFormChange("last_working_date", e.target.value)
                  }
                />

                <Select
                  label="Employment Type *"
                  value={form.employment_type}
                  onChange={(e) =>
                    onFormChange("employment_type", e.target.value)
                  }
                >
                  <option value="FULL_TIME">FULL_TIME</option>
                  <option value="PART_TIME">PART_TIME</option>
                  <option value="INTERN">INTERN</option>
                  <option value="CONTRACT">CONTRACT</option>
                </Select>

                <Select
                  label="Status *"
                  value={form.status}
                  onChange={(e) => onFormChange("status", e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                  <option value="RESIGNED">RESIGNED</option>
                </Select>

                <Input
                  label="Department *"
                  value={form.department}
                  onChange={(e) => onFormChange("department", e.target.value)}
                />
                <Input
                  label="Designation *"
                  value={form.designation}
                  onChange={(e) => onFormChange("designation", e.target.value)}
                />

                <Input
                  label="Work Location"
                  value={form.work_location}
                  onChange={(e) => onFormChange("work_location", e.target.value)}
                />
                <Input
                  label="Reporting Manager"
                  value={form.reporting_manager_name}
                  onChange={(e) =>
                    onFormChange("reporting_manager_name", e.target.value)
                  }
                />

                <Input
                  label="Probation Months"
                  type="number"
                  value={form.probation_months}
                  onChange={(e) =>
                    onFormChange("probation_months", e.target.value)
                  }
                />

                <Input
                  label="Experience (years)"
                  type="number"
                  step="0.1"
                  value={form.total_experience_years}
                  onChange={(e) =>
                    onFormChange("total_experience_years", e.target.value)
                  }
                />
              </div>

              <Input
                label="Primary Skills (comma separated)"
                value={form.primary_skills}
                onChange={(e) => onFormChange("primary_skills", e.target.value)}
                placeholder="Django, DRF, React"
              />

              <Input
                label="Education *"
                value={form.education}
                onChange={(e) => onFormChange("education", e.target.value)}
              />

              <Textarea
                label="Notes"
                value={form.notes}
                onChange={(e) => onFormChange("notes", e.target.value)}
              />
            </div>

            <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
              <SectionTitle>Salary</SectionTitle>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Basic *"
                  type="number"
                  value={form.salary_basic}
                  onChange={(e) => onFormChange("salary_basic", e.target.value)}
                />
                <Input
                  label="HRA *"
                  type="number"
                  value={form.salary_hra}
                  onChange={(e) => onFormChange("salary_hra", e.target.value)}
                />
                <Input
                  label="Special Allowance *"
                  type="number"
                  value={form.salary_special_allowance}
                  onChange={(e) =>
                    onFormChange("salary_special_allowance", e.target.value)
                  }
                />
                <Input
                  label="Bonus"
                  type="number"
                  value={form.salary_bonus}
                  onChange={(e) => onFormChange("salary_bonus", e.target.value)}
                />
                <Input
                  label="Deductions"
                  type="number"
                  value={form.salary_deductions}
                  onChange={(e) =>
                    onFormChange("salary_deductions", e.target.value)
                  }
                />
                <Input
                  label="In-hand Salary (Monthly)"
                  type="number"
                  value={form.in_hand_salary}
                  onChange={(e) => onFormChange("in_hand_salary", e.target.value)}
                  placeholder="If empty, will auto = net"
                />
                <Input
                  label="Currency"
                  value={form.currency}
                  onChange={(e) => onFormChange("currency", e.target.value)}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-secondary/15 bg-white/70 p-4 space-y-3">
              <SectionTitle>Personal + Bank</SectionTitle>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Date of Birth"
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => onFormChange("date_of_birth", e.target.value)}
                />
                <Input
                  label="Gender"
                  value={form.gender}
                  onChange={(e) => onFormChange("gender", e.target.value)}
                />
                <Input
                  label="Aadhaar Number"
                  value={form.aadhar_number}
                  onChange={(e) => onFormChange("aadhar_number", e.target.value)}
                />
                <Input
                  label="Bank Name"
                  value={form.bank_name}
                  onChange={(e) => onFormChange("bank_name", e.target.value)}
                />
                <Input
                  label="Bank Account No"
                  value={form.bank_account_no}
                  onChange={(e) =>
                    onFormChange("bank_account_no", e.target.value)
                  }
                />
                <Input
                  label="IFSC Code"
                  value={form.ifsc_code}
                  onChange={(e) => onFormChange("ifsc_code", e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Emergency Contact Name"
                  value={form.emergency_contact_name}
                  onChange={(e) =>
                    onFormChange("emergency_contact_name", e.target.value)
                  }
                />
                <Input
                  label="Emergency Contact Phone"
                  value={form.emergency_contact_phone}
                  onChange={(e) =>
                    onFormChange("emergency_contact_phone", e.target.value)
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <div className="text-xs text-secondary/60">
              * required fields: first_name, last_name, email, phone,
              date_of_joining, employment_type, department, designation, status,
              education, pan_number, salary_basic, salary_hra,
              salary_special_allowance
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setFormOpen(false)}
                className="rounded-xl border border-secondary/30 px-4 py-2 text-sm text-secondary bg-white/70 hover:bg-primary/10 transition"
                disabled={saving}
              >
                Cancel
              </button>

              {formMode === "create" ? (
                <button
                  onClick={submitCreate}
                  className={cls(
                    "rounded-xl bg-secondary text-accent px-5 py-2 text-sm font-semibold transition",
                    saving
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:opacity-95"
                  )}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Create Employee"}
                </button>
              ) : (
                <button
                  onClick={submitEdit}
                  className={cls(
                    "rounded-xl bg-secondary text-accent px-5 py-2 text-sm font-semibold transition",
                    saving
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:opacity-95"
                  )}
                  disabled={saving}
                >
                  {saving ? "Updating…" : "Update Employee"}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmModal
        open={deleteOpen}
        title="Delete Employee"
        message={`Are you sure you want to delete this employee (ID: ${getEmployeeCode(
          deleteTarget || deleteTarget?.id
        )})? This action cannot be undone.`}
        onClose={() => {
          if (!deleting) {
            setDeleteOpen(false);
            setDeleteTarget(null);
          }
        }}
        onConfirm={doDelete}
        loading={deleting}
      />
    </div>
  );
}
